// recover the tab when Vite fails to fetch a lazy-loaded chunk. after a new
// build is deployed, the previous build's content-hashed chunk filenames no
// longer exist on the server; an idle tab still holds the old index.html, so a
// route-level `import()` requests a stale filename. the server answers the
// missing asset (and the browser rejects a non-JS body under a .js URL, hence
// the "disallowed MIME type" error). pulling a fresh index.html via a full
// reload rewires the app to the current chunk hashes.

const RELOAD_GUARD_KEY = 'fp3:preload-error-reload-at'

// if a reload does not resolve the failure (the chunk is genuinely gone), a
// second preload error fires almost immediately — this window suppresses that
// repeat so the tab never loops. a legitimately stale chunk hit later, after a
// subsequent deploy, falls outside the window and reloads again.
export const RELOAD_GUARD_WINDOW_MS = 10_000

// decide whether a preload error should trigger a reload, recording the attempt
// so an immediate repeat within the guard window is suppressed. exported for
// direct unit testing of the loop guard.
export const shouldReloadOnPreloadError = (now: number, storage: Storage): boolean => {
  const previous = Number(storage.getItem(RELOAD_GUARD_KEY)) || 0
  if (previous && now - previous < RELOAD_GUARD_WINDOW_MS) return false
  storage.setItem(RELOAD_GUARD_KEY, String(now))
  return true
}

// attach the `vite:preloadError` listener; returns an unsubscribe function. the
// reload is injectable so the wiring can be exercised without navigating jsdom.
export const registerPreloadErrorReload = (reload: () => void = () => window.location.reload()) => {
  const handler = (event: WindowEventMap['vite:preloadError']) => {
    // stop Vite from rethrowing the error so the failure becomes a clean reload
    event.preventDefault()
    if (shouldReloadOnPreloadError(Date.now(), window.sessionStorage)) reload()
  }
  window.addEventListener('vite:preloadError', handler)
  return () => window.removeEventListener('vite:preloadError', handler)
}
